import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import assert from "assert"
import gsap from "gsap"
import {
  cameraPosition,
  color,
  float,
  Fn,
  fract,
  instancedBufferAttribute,
  length,
  min,
  positionLocal,
  positionWorld,
  round,
  sin,
  smoothstep,
  step,
  texture,
  time,
  uniform,
  uv,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import MemoryText from "./MemoryText"
import { Memory } from "./types"

export default class Memories {
  scene: THREE.Scene
  debugFolder?: FolderApi
  spacing: number

  cubeVerticesAttribute!: THREE.BufferAttribute
  instancePositionAttribute!: THREE.InstancedBufferAttribute
  paperInstanceOpacityAttribute!: THREE.InstancedBufferAttribute
  pointsMaterial!: THREE.MeshBasicNodeMaterial
  lineMaterial!: THREE.LineBasicNodeMaterial
  paperMaterial!: THREE.MeshBasicNodeMaterial
  instancedPoints!: THREE.Mesh
  instancedPaper!: THREE.Mesh
  vertices: number[][] = []
  paperInstancePositions: [number, number, number][] = []
  experience: Experience
  camera: THREE.PerspectiveCamera
  raycaster: THREE.Raycaster
  canvas: HTMLCanvasElement
  mouseClickCoords: THREE.Vector2
  isHoveringPaper: boolean = false
  isInMemoryView: boolean = false
  memories: Memory[]
  messageText!: MemoryText
  perlinTexture: THREE.Texture
  fadeInProgress!: THREE.UniformNode<number>
  faceDepthTextures: THREE.Texture[]
  faceTextureIndexAttribute!: THREE.InstancedBufferAttribute

  settings = {
    maxDistance: uniform(8), // Distance beyond which memories fade out
    minDistance: uniform(0.5), // Distance below which memories are fully visible
    closeDistance: uniform(float(0.3)), // Very close distance threshold
    emissiveIntensity: uniform(0.5),

    scalinCount: uniform(200),
    flickerIntensity: uniform(0.1),
    borderThickness: uniform(0.01),
    paperEmissiveIntensity: uniform(0.055),
    faceTextureBrightness: uniform(1.2),
  }

  constructor({
    scene,
    debugFolder,
    spacing,
    memories,
    camera,
    perlinTexture,
    faceDepthTextures,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
    spacing: number
    memories: Memory[]
    camera: THREE.PerspectiveCamera
    perlinTexture: THREE.Texture
    faceDepthTextures: THREE.Texture[]
  }) {
    this.memories = memories
    this.scene = scene
    this.debugFolder = debugFolder
    this.experience = Experience.getInstance()
    this.canvas = this.experience.renderer.instance.domElement
    this.camera = camera
    this.perlinTexture = perlinTexture
    this.spacing = spacing
    this.faceDepthTextures = faceDepthTextures
    this.raycaster = new THREE.Raycaster()
    this.mouseClickCoords = new THREE.Vector2()

    this.fadeInProgress = uniform(0)

    this.createMaterials()

    this.createMemories(memories)

    this.createMessageText()

    this.setDebug()

    this.setEvents()
  }

  setEvents() {
    this.canvas.addEventListener("click", this.clickHandler.bind(this))

    this.experience.on(EVENTS.Guestbook.BeforeEnter, () => {
      this.fadeInProgress.value = 0
    })

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.onEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Guestbook.Memory.Click,
      this.onMemoryClick.bind(this),
    )

    this.experience.on(
      EVENTS.Guestbook.Memory.Close,
      this.onMemoryClose.bind(this),
    )
  }

  onMemoryClick(event: unknown) {
    const { index } = event as { index: number }
    if (index === undefined || !this.paperInstanceOpacityAttribute) {
      return
    }

    const array = this.paperInstanceOpacityAttribute.array as Float32Array
    const proxy = { value: array[index] }

    gsap.to(proxy, {
      value: 0,
      duration: 1,
      delay: 1.5,
      ease: "power2.out",
      onUpdate: () => {
        array[index] = proxy.value
        this.paperInstanceOpacityAttribute.needsUpdate = true
      },
    })
  }

  onMemoryClose(event: unknown) {
    const { index } = event as { index: number }
    if (index === undefined || !this.paperInstanceOpacityAttribute) {
      return
    }

    const array = this.paperInstanceOpacityAttribute.array as Float32Array
    const proxy = { value: array[index] }

    gsap.to(proxy, {
      value: 1,
      duration: 1,
      delay: 1,
      ease: "power2.out",
      onUpdate: () => {
        array[index] = proxy.value
        this.paperInstanceOpacityAttribute.needsUpdate = true
      },
    })
  }

  onEnterAnimation(event: unknown) {
    const { progress } = event as { progress: number }
    if (progress == 1 && this.fadeInProgress.value < 1) {
      gsap.to(this.fadeInProgress, {
        value: 1,
        duration: 1,
        ease: "power2.out",
      })
    }
  }

  createMessageText() {
    this.messageText = new MemoryText({
      scene: this.scene,
      debugFolder: this.debugFolder,
    })
  }

  createMaterials() {
    // Distribute cubes along the grid
    const cubeSize = this.spacing / 2
    const halfSize = cubeSize / 2

    // Create 8 vertices of a cube (centered at origin)
    this.vertices = [
      [-halfSize, -halfSize, -halfSize], // 0: bottom-left-back
      [halfSize, -halfSize, -halfSize], // 1: bottom-right-back
      [halfSize, halfSize, -halfSize], // 2: top-right-back
      [-halfSize, halfSize, -halfSize], // 3: top-left-back
      [-halfSize, -halfSize, halfSize], // 4: bottom-left-front
      [halfSize, -halfSize, halfSize], // 5: bottom-right-front
      [halfSize, halfSize, halfSize], // 6: top-right-front
      [-halfSize, halfSize, halfSize], // 7: top-left-front
    ]

    // Create points positions array (8 vertices of the cube)
    const pointsPositions = this.vertices.flatMap((v) => [v[0], v[1], v[2]])

    // This is the base geometry for cube vertices (not instanced)
    this.cubeVerticesAttribute = new THREE.BufferAttribute(
      new Float32Array(pointsPositions),
      3,
    )

    //
    const distanceFromCamera = length(positionWorld.sub(cameraPosition))
    // Calculate opacity based on distance
    // Normal distance-based opacity: fully visible when distance <= minDistance,
    // fades out as distance increases beyond minDistance, invisible when distance >= maxDistance
    const normalOpacity = smoothstep(
      this.settings.maxDistance,
      this.settings.minDistance,
      distanceFromCamera,
    )
    // Close opacity: ensures full visibility when very close to camera (below closeDistance)
    // Returns 1 when distance is very small, smoothly fades as distance increases to closeDistance
    const closeOpacity = smoothstep(
      float(0),
      this.settings.closeDistance,
      distanceFromCamera,
    ).oneMinus()
    // Use maximum of both to ensure visibility at close and normal distances
    const distanceOpacity = normalOpacity.max(closeOpacity)

    /**
     * Points material
     * Note: colorNode and positionNode will be updated in createMemories()
     * after instance attributes are created
     */
    this.pointsMaterial = new THREE.MeshBasicNodeMaterial({
      colorNode: uniform(color("#000000")), // Will be updated to use instance colors
      opacityNode: distanceOpacity.mul(this.fadeInProgress),
      transparent: true,
      alphaToCoverage: true,
    })

    /**
     * Line material
     * Note: colorNode will be updated in createMemories()
     * after instance color attribute is created
     */
    this.lineMaterial = new THREE.LineBasicNodeMaterial({
      colorNode: uniform(color("#000000")), // Will be updated to use instance colors
      transparent: true,
      opacityNode: distanceOpacity.mul(this.fadeInProgress),
      alphaToCoverage: true,
    })

    /**
     * Paper material
     * Note: colorNode and positionNode will be updated in createMemories()
     * after instance attributes are created
     */
    this.paperMaterial = new THREE.MeshBasicNodeMaterial({
      colorNode: uniform(color("#000000")), // Will be updated to use instance colors
      opacityNode: distanceOpacity.mul(this.fadeInProgress),
      transparent: true,
      alphaToCoverage: true,
      side: THREE.DoubleSide,
    })
  }

  createMemories(memories: Memory[]) {
    // Grid settings (matching Grid.ts)
    const spacing = this.spacing
    const xSize = 10
    const ySize = 8
    const zSize = 40
    const xStart = -5
    const yStart = 2
    const zStart = -2
    const skipPercentage = 0.97
    // Camera scroll path: from (1, 3.2, 5) to (1, 3.2, -5)
    // Convert to grid coordinates:
    // x: 1 = (-5 + x) * 0.5 => x = 7
    // y: 3.2 = (2 + y) * 0.5 + 0.25 => y ≈ 4
    // z: 5 = (-2 - z) * 0.5 => z = -12, -5 = (-2 - z) * 0.5 => z = 8
    const cameraPathX = 7
    const cameraPathY = 4
    const cameraPathZStart = -12 // Start of camera path in grid coordinates
    const cameraPathZEnd = 8 // End of camera path in grid coordinates

    // Helper function to map index to 3D coordinates (similar to Grid.ts)
    const mapTo3D = (i: number) => {
      const z = Math.floor(i / (xSize * ySize))
      i -= z * xSize * ySize
      const y = Math.floor(i / xSize)
      const x = i % xSize
      return { x, y, z }
    }

    // Helper function to check if position is on camera path
    const isOnCameraPath = (x: number, y: number, z: number) => {
      return (
        x === cameraPathX &&
        y === cameraPathY &&
        z >= cameraPathZStart &&
        z <= cameraPathZEnd
      )
    }

    // First pass: collect valid memories and their positions/colors
    const validMemories: Array<{
      memory: Memory
      position: [number, number, number]
    }> = []

    let memoryIndex = 0 // Index for memories array
    let positionIndex = 0 // Index for matrix positions

    const instancePositions: number[] = []

    while (memoryIndex < memories.length) {
      const { x, y, z } = mapTo3D(positionIndex)

      if (z > zSize) {
        break
      }

      // Randomly skip this position based on percentage
      // Skip positions along the camera scroll path
      if (Math.random() < skipPercentage || isOnCameraPath(x, y, z)) {
        positionIndex++
        continue
      }

      const memory = memories[memoryIndex]

      // Convert grid coordinates to world coordinates
      const worldX = (xStart + x) * spacing
      const worldY = (yStart + y) * spacing + this.spacing / 2
      const worldZ = (zStart - z) * spacing

      validMemories.push({
        memory,
        position: [worldX, worldY, worldZ],
      })

      instancePositions.push(worldX, worldY, worldZ)

      memoryIndex++
      positionIndex++
    }

    if (validMemories.length === 0) {
      return
    }

    this.instancePositionAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(instancePositions),
      3,
    )

    // Create per-instance opacity attribute (initialized to 1.0 for all instances)
    const instanceOpacities = new Float32Array(validMemories.length).fill(1.0)
    this.paperInstanceOpacityAttribute = new THREE.InstancedBufferAttribute(
      instanceOpacities,
      1,
    )

    // Create per-instance face texture index attribute (random index for each paper)
    const textureCount = this.faceDepthTextures.length
    const faceTextureIndices = new Float32Array(validMemories.length)
    for (let i = 0; i < validMemories.length; i++) {
      faceTextureIndices[i] = Math.floor(Math.random() * textureCount)
    }
    this.faceTextureIndexAttribute = new THREE.InstancedBufferAttribute(
      faceTextureIndices,
      1,
    )

    // Pre-calculate all positions and colors in a single loop for better performance
    // Each memory gets 8 instances (one for each cube vertex)
    // Pre-compute final world positions (instancePos + vertex) to avoid redundant data
    const allPositions: number[] = []

    validMemories.forEach(({ position: instancePos }) => {
      // For each of the 8 cube vertices, calculate final position and assign color
      this.vertices.forEach((vertex) => {
        // Pre-compute final world position: instancePos + vertex offset
        allPositions.push(
          vertex[0] + instancePos[0],
          vertex[1] + instancePos[1],
          vertex[2] + instancePos[2],
        )
      })
    })

    const allPositionsAttribute = new THREE.InstancedBufferAttribute(
      new Float32Array(allPositions),
      3,
    )

    // Update materials to use instanced attributes
    // Each point gets its pre-calculated position and color
    // Since allPositionsAttribute contains the final world positions and BoxGeometry is centered,
    // we add the pre-computed position to positionLocal
    this.pointsMaterial.positionNode = positionLocal.add(
      instancedBufferAttribute(allPositionsAttribute),
    )
    this.pointsMaterial.colorNode = color("#2b414c")

    // Distance-based fade for emissive (same logic as paper opacity: fade when far or too close)
    const emissiveDistanceFade = Fn(() => {
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))
      const normalOpacity = smoothstep(
        this.settings.maxDistance,
        this.settings.minDistance,
        distanceFromCamera,
      )
      const closeOpacity = smoothstep(
        float(0),
        this.settings.closeDistance,
        distanceFromCamera,
      ).oneMinus()
      return normalOpacity.max(closeOpacity)
    })()

    ;(
      this.pointsMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = color("#2b414c")
      .mul(this.settings.emissiveIntensity)
      .mul(this.fadeInProgress)
      .mul(emissiveDistanceFade)

    this.lineMaterial.colorNode = color("#2b414c")
    this.lineMaterial.positionNode = positionLocal.add(
      instancedBufferAttribute(this.instancePositionAttribute),
    )
    ;(
      this.lineMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = color("#2b414c")
      .mul(this.settings.emissiveIntensity)
      .mul(this.fadeInProgress)
      .mul(emissiveDistanceFade)

    // Create base geometry for lines (12 edges = 24 vertices)
    const edges = [
      [0, 1], // bottom-back edge
      [1, 2], // right-back edge
      [2, 3], // top-back edge
      [3, 0], // left-back edge
      [4, 5], // bottom-front edge
      [5, 6], // right-front edge
      [6, 7], // top-front edge
      [7, 4], // left-front edge
      [0, 4], // bottom-left edge
      [1, 5], // bottom-right edge
      [2, 6], // top-right edge
      [3, 7], // top-left edge
    ]

    const linePositions: number[] = []
    edges.forEach((edge) => {
      const [v1, v2] = edge
      linePositions.push(...this.vertices[v1], ...this.vertices[v2])
    })

    const lineGeometry = new THREE.BufferGeometry()
    lineGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(linePositions), 3),
    )

    // Add UV attribute for node materials (required by LineBasicNodeMaterial)
    const lineUvs = new Float32Array((linePositions.length / 3) * 2)
    for (let i = 0; i < lineUvs.length / 2; i++) {
      lineUvs[i * 2] = 0.5 // center UV
      lineUvs[i * 2 + 1] = 0.5
    }
    lineGeometry.setAttribute("uv", new THREE.BufferAttribute(lineUvs, 2))

    // Add instance attributes to line geometry
    lineGeometry.setAttribute(
      "instancePosition",
      this.instancePositionAttribute,
    )

    // Create instanced mesh for points at cube vertices
    const pointsGeometry = new THREE.BoxGeometry(0.01, 0.01, 0.01)
    // Add instance attributes to geometry
    // Using single pre-computed position attribute for better performance
    pointsGeometry.setAttribute("instancePosition", allPositionsAttribute)

    this.instancedPoints = new THREE.Mesh(pointsGeometry, this.pointsMaterial)
    this.instancedPoints.frustumCulled = false
    this.instancedPoints.count = validMemories.length * 8 // Total number of points (memories * 8 vertices)
    this.scene.add(this.instancedPoints)

    // Create instanced LineSegments object
    const instancedLines = new THREE.LineSegments(
      lineGeometry,
      this.lineMaterial,
    )
    instancedLines.frustumCulled = false
    instancedLines.count = validMemories.length
    this.scene.add(instancedLines)

    //
    // PAPER
    //
    const cubeSize = this.spacing / 2
    const paperSize = cubeSize * 0.7 // Make paper 70% of cube size
    const paperWidth = paperSize
    const paperHeight = paperSize

    const paperGeometry = new THREE.PlaneGeometry(paperWidth, paperHeight)
    paperGeometry.setAttribute(
      "instancePosition",
      this.instancePositionAttribute,
    )
    paperGeometry.setAttribute(
      "instanceOpacity",
      this.paperInstanceOpacityAttribute,
    )
    paperGeometry.setAttribute(
      "faceTextureIndex",
      this.faceTextureIndexAttribute,
    )

    // Update paper material to use instanced attributes
    this.paperMaterial.positionNode = positionLocal.add(
      instancedBufferAttribute(this.instancePositionAttribute),
    )
    this.paperMaterial.colorNode = color("#2b414c")

    this.paperMaterial.opacityNode = Fn(() => {
      const uvNode = uv()

      // Calculate distance from center (normalized UV space: 0 to ~0.707)
      const dist = length(uvNode.sub(0.5).mul(1.1))

      // Create hole effect: visible in center, fades toward edges
      // Use smoothstep to create a falloff from center (dist=0) to edge (dist~0.707)
      const hole = smoothstep(1, 0.3, dist).oneMinus()

      // Sample a small portion of the perlin texture for variation
      const perlinUV = uvNode.mul(0.2)
      const perlinSample = texture(this.perlinTexture, perlinUV).r.pow(2).mul(2)

      const randomizedHole = hole.mul(perlinSample).clamp(0.1, 0.6)

      // Borders
      const distFromLeft = uvNode.x
      const distFromRight = uvNode.x.oneMinus()
      const distFromBottom = uvNode.y
      const distFromTop = uvNode.y.oneMinus()
      const distFromEdge = min(
        min(distFromLeft, distFromRight),
        min(distFromBottom, distFromTop),
      )
      const borderMask = step(distFromEdge, this.settings.borderThickness)

      // Horizontal scanlines effect
      const scanlinePattern = fract(uvNode.y.mul(this.settings.scalinCount))
      const lines = step(0.5, scanlinePattern).mul(0.7).add(0.3)

      // Display frequency flicker (simulating CRT refresh rate)
      const timeNode = time
      const flicker = sin(timeNode.mul(60))
        .mul(this.settings.flickerIntensity)
        .add(1)

      // Calculate distance-based opacity (same as other materials)
      const distanceFromCamera = length(positionWorld.sub(cameraPosition))
      const normalOpacity = smoothstep(
        this.settings.maxDistance,
        this.settings.minDistance,
        distanceFromCamera,
      )
      const closeOpacity = smoothstep(
        float(0),
        this.settings.closeDistance,
        distanceFromCamera,
      ).oneMinus()
      const distanceOpacity = normalOpacity.max(closeOpacity)

      // Sample face depth texture to modulate opacity
      // White areas (high values) increase opacity, dark areas (low values) decrease opacity
      // Get the texture index for this instance (rounded to ensure clean integer)
      const textureIndex = round(
        instancedBufferAttribute(this.faceTextureIndexAttribute),
      )

      // Sample all available face depth textures
      const textureSamples = this.faceDepthTextures.map((tex) =>
        texture(tex, uvNode).r.pow(3).mul(2),
      )

      // Select the correct texture sample based on instance index
      // Use weighted sum: for each texture, multiply by 1 if index matches, 0 otherwise
      // weight_i = step(i, idx) * step(idx, i) = 1 only when idx == i
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let depthSample: any = float(0)
      for (let i = 0; i < textureSamples.length; i++) {
        // Create a mask that's 1 only when textureIndex == i
        // step(i, idx) is 1 when idx >= i
        // step(idx, i) is 1 when i >= idx
        // Both are 1 only when idx == i
        const mask = step(float(i), textureIndex).mul(
          step(textureIndex, float(i)),
        )
        depthSample = depthSample.add(textureSamples[i].mul(mask))
      }
      depthSample = depthSample.mul(this.settings.faceTextureBrightness)

      // Get per-instance opacity (only affects the face depth, not the entire paper)
      const instanceOpacity = instancedBufferAttribute(
        this.paperInstanceOpacityAttribute,
      )

      // Apply instanceOpacity only to depthSample so only the face disappears, not the paper
      const faceDepth = depthSample.mul(instanceOpacity)

      return borderMask
        .add(lines)
        .mul(randomizedHole)
        .add(faceDepth)
        .mul(flicker)
        .mul(distanceOpacity)
        .mul(this.fadeInProgress)
    })()
    ;(
      this.paperMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = color("#2b414c")
      .mul(this.settings.paperEmissiveIntensity)
      .mul(this.fadeInProgress)
      .mul(emissiveDistanceFade)

    this.instancedPaper = new THREE.Mesh(paperGeometry, this.paperMaterial)
    this.instancedPaper.frustumCulled = false
    this.instancedPaper.count = validMemories.length
    this.scene.add(this.instancedPaper)

    // Store instance positions for click detection
    this.paperInstancePositions = validMemories.map(({ position }) => position)
  }

  clickHandler(event: MouseEvent) {
    // Calculate mouse position in normalized device coordinates (-1 to +1)
    const rect = this.canvas.getBoundingClientRect()
    this.mouseClickCoords.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouseClickCoords.y =
      -((event.clientY - rect.top) / rect.height) * 2 + 1

    // Find camera in the scene by name
    const camera = this.camera

    assert(camera, "Camera not found")

    this.raycaster.setFromCamera(this.mouseClickCoords, camera)

    const ray = this.raycaster.ray
    const cubeSize = this.spacing / 2
    const paperSize = cubeSize * 0.7
    const paperWidth = paperSize
    const paperHeight = paperSize * Math.SQRT2

    // Paper planes are oriented facing forward (normal along Z axis)
    const planeNormal = new THREE.Vector3(0, 0, 1)
    const halfWidth = paperWidth / 2
    const halfHeight = paperHeight / 2

    let closestIndex = -1
    let closestDistance = Infinity

    this.paperInstancePositions.forEach((position, index) => {
      const planePoint = new THREE.Vector3(
        position[0],
        position[1],
        position[2],
      )

      // Calculate ray-plane intersection
      // Ray equation: P = ray.origin + t * ray.direction
      // Plane equation: (P - planePoint) · planeNormal = 0
      const denom = ray.direction.dot(planeNormal)

      if (Math.abs(denom) > 0.0001) {
        // Ray is not parallel to plane
        const t = planePoint.clone().sub(ray.origin).dot(planeNormal) / denom

        if (t > 0) {
          // Intersection is in front of the ray origin
          const intersectionPoint = ray.origin
            .clone()
            .add(ray.direction.clone().multiplyScalar(t))

          // Check if intersection point is within the paper bounds
          // Paper is centered at position, so check if point is within bounds
          const localX = intersectionPoint.x - position[0]
          const localY = intersectionPoint.y - position[1]

          if (Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight) {
            // Intersection is within paper bounds
            const distance = ray.origin.distanceTo(intersectionPoint)
            if (distance < closestDistance) {
              closestDistance = distance
              closestIndex = index
            }
          }
        }
      }
    })

    if (closestIndex >= 0) {
      // Set cursor to default when entering memory view
      this.canvas.style.cursor = "default"
      this.isInMemoryView = true

      const event = {
        index: closestIndex,
        memory: this.paperInstancePositions[closestIndex],
        memoriesInstance: this,
      }

      // this.updateMessageText(event)

      this.experience.trigger(EVENTS.Guestbook.Memory.Click, [event])
    }
  }

  update() {
    this.checkHover()
  }

  private checkHover() {
    // Skip hover detection if in memory view
    if (this.isInMemoryView) {
      return
    }

    // Use pointer properties from Experience
    const mouseCoords = new THREE.Vector2(
      this.experience.pointer.ndcX,
      this.experience.pointer.ndcY,
    )

    const camera = this.camera

    assert(camera, "Camera not found")

    this.raycaster.setFromCamera(mouseCoords, camera)

    const ray = this.raycaster.ray
    const cubeSize = this.spacing / 2
    const paperSize = cubeSize * 0.7
    const paperWidth = paperSize
    const paperHeight = paperSize * Math.SQRT2

    // Paper planes are oriented facing forward (normal along Z axis)
    const planeNormal = new THREE.Vector3(0, 0, 1)
    const halfWidth = paperWidth / 2
    const halfHeight = paperHeight / 2

    let isHovering = false

    this.paperInstancePositions.forEach((position) => {
      const planePoint = new THREE.Vector3(
        position[0],
        position[1],
        position[2],
      )

      // Calculate ray-plane intersection
      const denom = ray.direction.dot(planeNormal)

      if (Math.abs(denom) > 0.0001) {
        // Ray is not parallel to plane
        const t = planePoint.clone().sub(ray.origin).dot(planeNormal) / denom

        if (t > 0) {
          // Intersection is in front of the ray origin
          const intersectionPoint = ray.origin
            .clone()
            .add(ray.direction.clone().multiplyScalar(t))

          // Check if intersection point is within the paper bounds
          const localX = intersectionPoint.x - position[0]
          const localY = intersectionPoint.y - position[1]

          if (Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight) {
            // Intersection is within paper bounds
            isHovering = true
          }
        }
      }
    })

    // Update cursor only if hover state changed
    if (isHovering !== this.isHoveringPaper) {
      this.isHoveringPaper = isHovering
      this.canvas.style.cursor = isHovering ? "pointer" : "default"
    }
  }

  updateMessageText(event: {
    index: number
    memory: [number, number, number]
  }) {
    const memory = this.memories[event.index]
    const date = new Date(memory.created_at)
    const formattedDate = `${date.toLocaleDateString("en-US", { month: "long" })}, ${date.getFullYear()}`
    this.messageText.text = `${memory.message}\n\n${memory.name}\n\n${formattedDate}\n${memory.origin}`

    // Update geometry with the new text
    this.messageText.geometry.update({
      text: this.messageText.text,
    })

    // Compute bounding box to get text dimensions for centering
    this.messageText.geometry.computeBoundingBox()
    const bbox = this.messageText.geometry.boundingBox

    if (bbox && !bbox.isEmpty()) {
      // Calculate the center of the bounding box
      const centerX = (bbox.min.x + bbox.max.x) / 2

      // Calculate bounding box height
      const bboxHeight = bbox.max.y - bbox.min.y

      // Apply scale to the offset (geometry is in font units, mesh is scaled)
      const scale = this.messageText.settings.scale

      // Position text centered on the paper plane
      // Paper plane is centered at memory position
      // For X: center horizontally by offsetting by centerX
      // For Y: the bottom of the text is currently at the center, so we need to move it up by half the height
      // Subtract half the height to move the text up and center it vertically
      this.messageText.mesh.position.set(
        event.memory[0] - centerX * scale,
        event.memory[1] - (bboxHeight / 2) * scale,
        event.memory[2] + 0.01,
      )
    } else {
      // Fallback: position at memory center
      this.messageText.mesh.position.set(
        event.memory[0],
        event.memory[1],
        event.memory[2] + 0.01,
      )
    }

    this.messageText.mesh.visible = true
  }

  removeMessageText() {}

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "⬛ Memories",
      expanded: false,
    })

    const settings = {
      emissiveIntensity: this.settings.emissiveIntensity.value,
      paperEmissiveIntensity: this.settings.paperEmissiveIntensity.value,
      flickerIntensity: this.settings.flickerIntensity.value,
      faceTextureBrightness: this.settings.faceTextureBrightness.value,
    }

    folder
      .addBinding(settings, "emissiveIntensity", {
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.emissiveIntensity.value = ev.value
      })

    folder
      .addBinding(settings, "paperEmissiveIntensity", {
        min: 0,
        max: 1,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.settings.paperEmissiveIntensity.value = ev.value
      })

    folder
      .addBinding(settings, "flickerIntensity", {
        min: 0,
        max: 0.1,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.settings.flickerIntensity.value = ev.value
      })

    folder
      .addBinding(settings, "faceTextureBrightness", {
        label: "Face brightness",
        min: 0,
        max: 3,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.faceTextureBrightness.value = ev.value
      })
  }
}
